import type { ReactNode } from 'react';
import { useEffect } from 'react';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { Box, ButtonBase, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';

import { AM, AM_TEXT } from '../assignmentMonitoringTokens';
import type { Chip } from '../assignmentMonitoring';

const PILL_TONES = {
  indigo: { bg: AM.indigo50, fg: AM.primary },
  grey: { bg: AM.actionSelected, fg: AM.muted },
  red: { bg: AM.red50, fg: AM.red700 },
} as const;

export function Pill({ label, tone }: Chip) {
  const colors = PILL_TONES[tone];
  return (
    <Box
      component="span"
      data-testid="am-pill"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        padding: '0 8px',
        borderRadius: '9999px',
        bgcolor: colors.bg,
        color: colors.fg,
        fontSize: '12px',
        lineHeight: '24px',
        fontWeight: 700,
        whiteSpace: 'nowrap',
      }}
    >
      {label}
    </Box>
  );
}

export function OffPill() {
  return (
    <Box
      component="span"
      data-testid="am-off-pill"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        padding: '0 12px',
        borderRadius: '9999px',
        bgcolor: AM.actionSelected,
        outline: `1px solid ${AM.divider}`,
        color: AM.muted,
        ...AM_TEXT.body1,
        fontWeight: 500,
        whiteSpace: 'nowrap',
      }}
    >
      OFF
    </Box>
  );
}

export interface NoteRowProps {
  children: ReactNode;
  right?: ReactNode;
  to?: string;
  onClick?: () => void;
}

// The card's status rows: a pending change, the previous month closing, a
// skipped month, a pending turn-off. Only the closing row links anywhere.
export function NoteRow({ children, right, to, onClick }: NoteRowProps) {
  const sx = {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '8px 12px',
    borderRadius: '8px',
    bgcolor: AM.indigo50,
    color: AM.primary,
    ...AM_TEXT.body2Bold,
    textAlign: 'left',
    width: '100%',
    boxSizing: 'border-box',
    textDecoration: 'none',
  } as const;
  const body = (
    <>
      <AccessTimeIcon sx={{ fontSize: 16, flexShrink: 0 }} />
      <Box component="span" sx={{ flex: '1 1 0' }}>
        {children}
      </Box>
      {right ? (
        <Box component="span" sx={{ fontWeight: 600 }}>
          {right}
        </Box>
      ) : null}
      {to ? <ChevronRightIcon sx={{ fontSize: 16, flexShrink: 0 }} /> : null}
    </>
  );
  if (to) {
    return (
      <Box component={RouterLink} to={to} onClick={onClick} data-testid="am-note" sx={{ ...sx, '&:hover': { bgcolor: AM.indigo100 } }}>
        {body}
      </Box>
    );
  }
  return (
    <Box data-testid="am-note" sx={sx}>
      {body}
    </Box>
  );
}

export interface MetBarProps {
  met: number;
  notMet: number;
  thin?: boolean;
}

export function MetBar({ met, notMet, thin }: MetBarProps) {
  return (
    <Box
      data-testid="am-met-bar"
      sx={{ display: 'flex', height: thin ? '4px' : '12px', borderRadius: '4px', overflow: 'hidden' }}
    >
      {met ? <Box sx={{ flexGrow: met, bgcolor: thin ? AM.indigo300 : AM.primary }} /> : null}
      {notMet ? <Box sx={{ flexGrow: notMet, bgcolor: thin ? AM.red200 : AM.red300 }} /> : null}
    </Box>
  );
}

export const DOT_COLORS = {
  met: AM.primary,
  notmet: AM.red300,
  notstarted: AM.red300,
  inprog: AM.orange300,
  toscore: AM.indigo200,
} as const;

export function Dot({ kind }: { kind: keyof typeof DOT_COLORS }) {
  return (
    <Box
      component="span"
      sx={{ width: 8, height: 8, borderRadius: '9999px', flexShrink: 0, display: 'inline-block', bgcolor: DOT_COLORS[kind] }}
    />
  );
}

export interface LinkButtonProps {
  label: string;
  onClick?: () => void;
  to?: string;
  muted?: boolean;
  disabled?: boolean;
  startIcon?: ReactNode;
  endIcon?: ReactNode;
}

// The card footer's text links: "Edit requirements" (muted) and "See details".
export function LinkButton({ label, onClick, to, muted, disabled, startIcon, endIcon }: LinkButtonProps) {
  const sx = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    padding: '0 8px 0 16px',
    borderRadius: '8px',
    color: muted ? AM.muted : AM.primary,
    fontFamily: AM.font,
    fontSize: '16px',
    lineHeight: '32px',
    fontWeight: 600,
    letterSpacing: '0.5px',
    textDecoration: 'none',
    '&:hover': { bgcolor: muted ? AM.actionHover : AM.indigo50 },
    '&.Mui-disabled': { color: AM.disabled },
  } as const;
  if (to) {
    return (
      <ButtonBase component={RouterLink} to={to} onClick={onClick} sx={sx}>
        {startIcon}
        {label}
        {endIcon}
      </ButtonBase>
    );
  }
  return (
    <ButtonBase onClick={onClick} disabled={disabled} sx={sx}>
      {startIcon}
      {label}
      {endIcon}
    </ButtonBase>
  );
}

export interface ToastProps {
  message: string | null;
  onDone: () => void;
}

export function Toast({ message, onDone }: ToastProps) {
  useEffect(() => {
    if (!message) return undefined;
    const timer = setTimeout(onDone, 4000);
    return () => clearTimeout(timer);
  }, [message, onDone]);
  if (!message) return null;
  return (
    <Box
      role="status"
      data-testid="am-toast"
      sx={{
        position: 'fixed',
        left: '50%',
        bottom: '40px',
        transform: 'translateX(-50%)',
        width: 400,
        maxWidth: 'calc(100% - 32px)',
        zIndex: 1400,
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        padding: '12px 16px',
        borderRadius: '8px',
        bgcolor: AM.ink,
        color: AM.inverted,
        boxShadow: AM.elevationFloating,
        ...AM_TEXT.body1,
      }}
    >
      <CheckCircleIcon sx={{ fontSize: 20, color: AM.success, flexShrink: 0 }} />
      <Typography component="span" sx={{ ...AM_TEXT.body1 }}>
        {message}
      </Typography>
    </Box>
  );
}

export function Skeleton({ width, height = 12, flex }: { width?: number | string; height?: number; flex?: number }) {
  return (
    <Box
      data-testid="am-skeleton"
      sx={{ display: 'block', bgcolor: AM.grey50, borderRadius: '8px', height, width, flex }}
    />
  );
}
